import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import styled from 'styled-components';
import { CorporateTheme } from '../../../utils/themes';
import { SkeletonCell } from '../../../components/SkeletonCell';
import StyledText from '../../../components/StyledComponents/StyledText';
import CloseIcon from '../../../assets/icons/CloseIcon';
import { Button } from '../../../components/form/Button';
import DocumentIcon from '../../../assets/icons/document-text';
import {
    useGetAllComponentsQuery,
    useGetAllFieldsQuery,
    useGetFieldByIdQuery,
    useApplyConditionalLogicToFieldMutation
} from '../../../api/formsApiSlice';
import { Field } from '../../../types/types';
import ConditionalFieldIcon from '../../../assets/icons/ConditonalFieldsIcon';
import SearchBar from '../../../components/searchBar/SearchBar';
import { Tabs } from '../../../components/Tabs/Tabs';
import { FieldLabel } from '../Components/CreateComponentModal';
import CustomInput from '../../../components/form/CustomInput';
import { useToast } from '../../../components/toast/ToastContext';
import CrossIcon from '../../../assets/icons/CrossIcon';
import { useTheme } from '../../../middleware/ThemeContext';

const Container = styled.div`
  display: flex;
  padding: 0.5rem;
  gap: 2rem;
  border: 1px solid ${CorporateTheme.colors.border};
  border-radius: ${CorporateTheme.borderRadius};
`;

const HeaderContainer = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 2rem 2rem;
  border-radius: ${CorporateTheme.borderRadius};
  overflow: hidden;

  &::before {
    content: "";
    background-image: url(${require("../../../assets/images/CreateFormBackground.png")});
    background-size: cover;
    background-position: center;
    position: absolute;
    inset: 0;
    z-index: 0;
    opacity: 0.9;
  }

  * {
    position: relative;
    z-index: 1;
  }
`;

const HeaderTitles = styled.div`
  text-align: center;
  max-width: 520px;
  font-family: "Raleway", sans-serif;
`;

const LeftPanel = styled.div`
  flex: 1;
  background: ${CorporateTheme.colors.panelBG};
  border: 1px dashed ${CorporateTheme.colors.border};
  border-radius: ${CorporateTheme.borderRadius};
  padding: 1rem;
`;

const RightPanel = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: 1rem;
  background:${CorporateTheme.colors.panelBG};
  border-radius: ${CorporateTheme.borderRadius};
`;

const FieldChip = styled.div<{ isDragging?: boolean }>`
  display: flex;
  width: 100%;
  align-items: center;
  gap: 8px;
  background: ${({ isDragging }) => (isDragging ? '#0556CB' : '#FFFFFF')};
  color: ${({ isDragging }) => (isDragging ? '#FFFFFF' : 'inherit')};
  padding: 8px;
  border-radius: 4px;
  border: 1px solid #BABDC1;
  height: 35px;
  font-weight: 500;
  font-family: "Raleway", sans-serif;
  cursor: ${({ isDragging }) => (isDragging ? 'grabbing' : 'grab')};

  user-select: none;
  &:active {
    cursor: grabbing;
  }
`;

const FieldsList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  overflow-y: auto;
  max-height: 23rem;
`;

const OptionDropArea = styled.div<{ className?: string }>`
  height: 60px;
  border-radius: 6px;
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 10px;
  background: white;
  transition: all 0.2s ease; 
  border: 2px dashed ${CorporateTheme.colors.border}; 
  ${({ className }) => className}

  &:hover {
    background: #0556CB1A;
  }

  &.drag-over {
    background: #0556CB1A;
    border: 1px dashed #0556CB;
  }
`;
const ConnectingLineContainer = styled.div`
  position: relative;
  margin-left: 2rem;
`;

const ConnectingLine = styled.div<{ hideLast?: boolean }>`
  position: absolute;
  left: -20px;
  top: 3%;
  bottom: ${({ hideLast }) => hideLast ? '26%' : '0'};
  width:0.1rem;
  background-color: ${CorporateTheme.colors.border};
`;

const OptionContainer = styled.div<{ isLast?: boolean }>`
  position: relative;
  padding-left: 0.8rem;
  ${({ isLast }) => isLast && `
    ${ConnectingLine} {
      display: none;
    }
  `}
`;

const OptionConnector = styled.div<{ isLast?: boolean }>`
  position: absolute;
  left: -20px;
  top: 22%;
  width: 20px;
  height: 0.1rem;
  background-color: ${CorporateTheme.colors.border};
//   ${({ isLast }) => isLast && 'display: none;'}
`;
const Footer = styled.div`
  margin-top: 1rem;
  display: flex;
  justify-content: flex-end;
  gap: 1rem;
`;
const DroppedChip = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  background: #FFFFFF;
  padding: 8px;
  border-radius: 4px;
  border: 1px solid ${CorporateTheme.colors.primary};
  height: 35px;
  font-weight: 500;
  font-family: "Raleway", sans-serif;
  margin: 4px 0;
`;

const RemoveButton = styled.button`
  background: none;
  border: none;
  cursor: pointer;
  padding: 0;
  display: flex;
  align-items: center;
`;
interface AttachedField {
    option: string;
    fieldId: number | null;
    componentId: number | null;
    fieldData?: Field;
    componentData?: any;
    type?: string;
}

interface ConditionalLogicPayload {
    controllingFieldId: number;
    attachedFields: AttachedField[];
}

interface ConditionalFieldModalProps {
    onClose: () => void;
    onSave?: () => void;
    fieldId?: number;
    controllingFields?: any[]; // Add this prop for pre-populated data
}

const ConditionalFieldModal: React.FC<ConditionalFieldModalProps> = ({
    onClose,
    onSave,
    fieldId,
    controllingFields = []
}) => {
    const { theme } = useTheme();
    const TABS = [
        { id: 1, title: "Fields" },
        { id: 2, title: "Components" },
    ];

    const [activeTab, setActiveTab] = useState<string>("Fields");
    const [searchTerm, setSearchTerm] = useState("");
    const [page, setPage] = useState(1);
    const [limit] = useState(20);
    const [isDragOver, setIsDragOver] = useState(false);
    const [controllingField, setControllingField] = useState<Field>({} as Field);
    const [payload, setPayload] = useState<ConditionalLogicPayload>({
        controllingFieldId: fieldId || 0,
        attachedFields: []
    });
    const [draggingChipId, setDraggingChipId] = useState<number | null>(null);
    const {
        data: fieldById,
        isFetching: isFetchingFieldById
    } = useGetFieldByIdQuery(fieldId);
    const handleDragStart = (e: React.DragEvent, label: string) => {
        const ghost = document.createElement('div');
        ghost.innerText = label;
        ghost.style.position = 'absolute';
        ghost.style.top = '-9999px';
        ghost.style.left = '-9999px';
        ghost.style.padding = '8px';
        ghost.style.borderRadius = '4px';
        ghost.style.background = '#0556CB';
        ghost.style.color = '#FFF';
        ghost.style.fontWeight = '500';
        ghost.style.fontFamily = 'Raleway, sans-serif';
        ghost.style.height = '35px';
        ghost.style.display = 'flex';
        ghost.style.alignItems = 'center';
        ghost.style.gap = '8px';
        ghost.style.cursor = 'grabbing !important';
        document.body.appendChild(ghost);
        e.dataTransfer.setDragImage(ghost, 0, 0);

        setTimeout(() => document.body.removeChild(ghost), 0);
    };
    const optionsListRef = useRef<HTMLDivElement>(null);
    const showToast = useToast();
    const [applyConditionalLogic] = useApplyConditionalLogicToFieldMutation();
    useEffect(() => {
        if (controllingFields && controllingFields.length > 0) {
            const initialAttachedFields = controllingFields.map((field) => ({
                option: field.conditionValue,
                fieldId: field.dependentFieldId,
                componentId: field.dependentComponentId,
                type: field.dependentFieldId ? 'field' : 'component',
                fieldData: field.dependentField,
                componentData: field.dependentComponent
            }));

            setPayload(prev => ({
                ...prev,
                attachedFields: initialAttachedFields
            }));
        }
    }, [controllingFields]);

    const tabs = useMemo(
        () => TABS.map(tab => ({ ...tab, active: tab.title === activeTab })),
        [activeTab]
    );

    const {
        data: fieldsData,
        refetch: refetchFields,
        isFetching: isFetchingFields,
    } = useGetAllFieldsQuery({
        query: activeTab === "Fields" ? searchTerm : "",
        page,
        limit
    });
    const {
        data: componentsData,
        refetch: refetchComponents,
        isFetching: isFetchingComponents,
    } = useGetAllComponentsQuery({
        query: activeTab === "Components" ? searchTerm : "",
        page,
        limit
    });

    const [fields, setFields] = useState<Field[]>([]);
    const [components, setComponents] = useState<any[]>([]);

    const handleTabChange = useCallback((tabId: number) => {
        const tab = TABS.find(t => t.id === tabId);
        if (tab) setActiveTab(tab.title);
        setSearchTerm('');
        setPage(1);
        if (tabId === 1) refetchFields();
        else if (tabId === 2) refetchComponents();
    }, [activeTab]);

    const handleScrollForFields = () => {
        if (optionsListRef.current && (fieldsData?.totalCount > fields.length)) {
            const { scrollTop, scrollHeight, clientHeight } = optionsListRef.current;
            if (scrollTop + clientHeight >= scrollHeight - 20) {
                setPage(prevPage => prevPage + 1);
            }
        }
    };

    const handleScrollForComponents = () => {
        if (optionsListRef.current && (componentsData?.totalCount > components.length)) {
            const { scrollTop, scrollHeight, clientHeight } = optionsListRef.current;
            if (scrollTop + clientHeight >= scrollHeight - 20) {
                setPage(prevPage => prevPage + 1);
            }
        }
    };

    const handleDragOver = useCallback((e: React.DragEvent<HTMLDivElement>) => {
        e.preventDefault();
        e.currentTarget.classList.add('drag-over');
        setIsDragOver(true);
    }, []);

    const handleDragLeave = useCallback((e: React.DragEvent<HTMLDivElement>) => {
        e.preventDefault();
        e.currentTarget.classList.remove('drag-over');
        setIsDragOver(false);
    }, []);

    const handleDrop = useCallback((e: React.DragEvent<HTMLDivElement>, option: string) => {
        e.preventDefault();
        e.currentTarget.classList.remove('drag-over');
        setIsDragOver(false)
        try {
            const data = JSON.parse(e.dataTransfer.getData("application/json"));
            const { type, id } = data;

            // Find the dropped item from fields or components
            const droppedItem = type === 'field'
                ? fields.find(f => f.id === id)
                : components.find(c => c.id === id);

            setPayload(prev => {
                const existingIndex = prev.attachedFields.findIndex(f => f.option === option);

                if (existingIndex >= 0) {
                    // Update existing entry
                    const updatedFields = [...prev.attachedFields];
                    updatedFields[existingIndex] = {
                        ...updatedFields[existingIndex],
                        fieldId: type === "field" ? id : null,
                        componentId: type === "component" ? id : null,
                        fieldData: type === "field" ? droppedItem : undefined,
                        componentData: type === "component" ? droppedItem : undefined,
                        type
                    };

                    return {
                        ...prev,
                        attachedFields: updatedFields
                    };
                } else {
                    // Add new entry
                    return {
                        ...prev,
                        attachedFields: [
                            ...prev.attachedFields,
                            {
                                option,
                                fieldId: type === "field" ? id : null,
                                componentId: type === "component" ? id : null,
                                fieldData: type === "field" ? droppedItem : undefined,
                                componentData: type === "component" ? droppedItem : undefined,
                                type
                            }
                        ]
                    };
                }
            });

            showToast("Success", `${type === "field" ? "Field" : "Component"} added to condition`, "success");
        } catch (error) {
            console.error("Drop failed:", error);
            showToast("Error", "Failed to add field/component to condition", "error");
        }
    }, [fields, components, showToast]);

    const handleSave = useCallback(async () => {
        try {
            // if (!payload.controllingFieldId || payload.attachedFields.length === 0) {
            //     showToast("Error", "Please configure at least one condition", "error");
            //     return;
            // }

            // Create a cleaned-up payload without fieldData, componentData, and type
            const cleanedPayload = {
                controllingFieldId: payload.controllingFieldId,
                attachedFields: payload.attachedFields.map(field => ({
                    option: field.option,
                    fieldId: field.fieldId,
                    componentId: field.componentId
                    // Only include these essential fields
                }))
            };

            await applyConditionalLogic(cleanedPayload).unwrap();

            showToast("Success", "Conditional logic applied successfully", "success");
            onSave && onSave();
        } catch (error) {
            console.error("Failed to apply conditional logic:", error);
            showToast("Error", "Failed to apply conditional logic", "error");
        }
    }, [payload, applyConditionalLogic, showToast, onSave]);
    useEffect(() => {
        if (fieldsData?.fields) {
            if (page === 1) {
                setFields(fieldsData?.fields);
            } else {
                setFields(prev => [...prev, ...fieldsData?.fields]);
            }
        }
    }, [fieldsData, page]);

    useEffect(() => {
        if (componentsData?.components) {
            if (page === 1) {
                setComponents(componentsData?.components);
            } else {
                setComponents(prev => [...prev, ...componentsData?.components]);
            }
        }
    }, [componentsData, page]);

    useEffect(() => {
        if (fieldById) {
            setControllingField(fieldById.field);
            setPayload(prev => ({
                ...prev,
                controllingField: fieldById.field.id
            }));
        }
    }, [fieldById]);
    return (
        <>
            <HeaderContainer>
                <HeaderTitles>
                    <StyledText
                        className="mb-2"
                        fontSize="20px"
                        fontWeight={600}
                        textColor={CorporateTheme.colors.primary}
                    >
                        Set Conditional Logic
                    </StyledText>
                    <StyledText
                        fontSize="14px"
                        fontWeight={500}
                        textColor={CorporateTheme.colors.textSecondary}
                    >
                        Apply the conditional fields against the options of selected field.
                    </StyledText>
                </HeaderTitles>
                <button
                    onClick={onClose}
                    style={{
                        background: "none",
                        border: "none",
                        cursor: "pointer",
                        padding: 0,
                        position: "absolute",
                        top: "0",
                        right: "0",
                    }}
                >
                    <CloseIcon />
                </button>
            </HeaderContainer>

            <Container>
                <LeftPanel>
                    <Tabs className="mb-3" tabs={tabs} onChangeTabs={handleTabChange} />

                    <StyledText
                        className="mt-2 mb-2"
                        fontSize="14px"
                        fontWeight={600}
                        textColor={CorporateTheme.colors.headerColor}
                    >
                        All {activeTab === "Fields" ? 'System and Custom Fields' : 'Components'} will show here
                    </StyledText>
                    <SearchBar
                        style={{ width: '100%', marginBottom: '1rem', backgroundColor: '#fff' }}
                        searchTerm={searchTerm}
                        onSearchChange={setSearchTerm} />

                    {activeTab === "Fields" && (
                        isFetchingFields ? (
                            <FieldsList>
                                {[1, 2, 3, 4, 5, 6, 7].map((i) => (
                                    <SkeletonCell
                                        key={i}
                                        style={{
                                            height: "20px",
                                            margin: "4px 0",
                                            borderRadius: "3px",
                                        }}
                                    />
                                ))}
                            </FieldsList>
                        ) : (
                            <FieldsList
                                className='max-h-40'
                                onScroll={handleScrollForFields}
                                ref={optionsListRef}
                            >
                                {fields.map((field: Field) => (
                                    <FieldChip
                                        key={field.id}
                                        draggable
                                        onDragStart={(e) => {
                                            setDraggingChipId(field.id ? field.id : null);
                                            handleDragStart(e, field?.name);
                                            e.dataTransfer.setData(
                                                "application/json",
                                                JSON.stringify({
                                                    type: "field",
                                                    id: field.id
                                                })
                                            );
                                        }}
                                        isDragging={draggingChipId === field?.id}
                                        onDragEnd={() => setDraggingChipId(null)}
                                    >
                                        <ConditionalFieldIcon />
                                        <StyledText fontSize="12px" fontWeight={600}>
                                            {field.name}
                                        </StyledText>
                                    </FieldChip>
                                ))}
                            </FieldsList>
                        )
                    )}

                    {activeTab === "Components" && (
                        isFetchingComponents ? (
                            <FieldsList>
                                {[1, 2, 3, 4, 5, 6, 7].map((i) => (
                                    <SkeletonCell
                                        key={i}
                                        style={{
                                            height: "20px",
                                            margin: "4px 0",
                                            borderRadius: "3px",
                                        }}
                                    />
                                ))}
                            </FieldsList>
                        ) : (
                            <FieldsList
                                className='max-h-40'
                                onScroll={handleScrollForComponents}
                                ref={optionsListRef}
                            >
                                {components.map((component: any) => (
                                    <FieldChip
                                        key={component.id}
                                        draggable
                                        onDragStart={(e) => {
                                            setDraggingChipId(component.id ? component.id : null);
                                            handleDragStart(e, component?.name);
                                            e.dataTransfer.setData(
                                                "application/json",
                                                JSON.stringify({
                                                    type: "component",
                                                    id: component.id
                                                })
                                            );
                                        }}
                                        isDragging={draggingChipId === component?.id}
                                        onDragEnd={() => setDraggingChipId(null)}
                                    >
                                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                            <ConditionalFieldIcon />
                                            <StyledText fontSize="12px" fontWeight={600}>{component.name}</StyledText>
                                        </div>
                                    </FieldChip>
                                ))}
                            </FieldsList>
                        )
                    )}
                </LeftPanel>

                <RightPanel>
                    <StyledText
                        className="mt-1 mb-3"
                        fontSize="14px"
                        fontWeight={600}
                        textColor={CorporateTheme.colors.headerColor}
                    >
                        Drop field on which you want to apply condition
                    </StyledText>

                    {isFetchingFieldById ? (
                        <div className="d-flex flex-column">
                            {[1, 2, 3, 4, 5].map((i) => (
                                <SkeletonCell
                                    key={i}
                                    style={{
                                        height: "40px",
                                        margin: "8px 0",
                                        borderRadius: CorporateTheme.borderRadius,
                                    }}
                                />
                            ))}
                        </div>
                    ) : (
                        <div className="d-flex flex-column">
                            <div className="d-flex align-items-center gap-2">
                                <FieldLabel theme={theme}>{controllingField.name}</FieldLabel>
                            </div>
                            <CustomInput
                                className="mt-1 bg-white"
                                placeholder="Enter condition value"
                                value={controllingField?.config?.uiMeta?.placeholder || ''}
                                disabled={true}
                                onChange={() => { }}
                            />
                            {controllingField?.config?.options && controllingField?.config?.options.length > 0 && (
                                <ConnectingLineContainer>
                                    <ConnectingLine />
                                    <div className="mt-2">
                                        {controllingField?.config?.options?.map((option: string, index: number) => {
                                            const isLast = (controllingField?.config?.options && index === controllingField.config.options.length - 1) || false;
                                            const attachedField = payload.attachedFields.find(f => f.option === option);

                                            return (
                                                <OptionContainer key={option} isLast={isLast}>
                                                    <OptionConnector isLast={isLast} />
                                                    <CustomInput
                                                        className="mt-1 bg-white"
                                                        placeholder={option}
                                                        value={option}
                                                        disabled={true}
                                                        onChange={() => { }}
                                                    />
                                                    <OptionDropArea
                                                        onDragOver={handleDragOver}
                                                        onDragLeave={handleDragLeave}
                                                        onDrop={(e) => handleDrop(e, option)}
                                                        className={`my-2 ${isDragOver ? 'drag-over' : ''}`}
                                                    >
                                                        {attachedField ? (
                                                            <DroppedChip>
                                                                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                                                    <ConditionalFieldIcon />
                                                                    <StyledText fontSize="12px" fontWeight={600}>
                                                                        {attachedField.type === 'field' ? (
                                                                            attachedField.fieldData?.name
                                                                        ) : (
                                                                            attachedField.componentData?.name
                                                                        )}
                                                                    </StyledText>
                                                                </div>
                                                                <RemoveButton
                                                                    onClick={() => {
                                                                        setPayload(prev => ({
                                                                            ...prev,
                                                                            attachedFields: prev.attachedFields.filter(f => f.option !== option)
                                                                        }));
                                                                    }}
                                                                >
                                                                    <CloseIcon size={'20'} />
                                                                </RemoveButton>
                                                            </DroppedChip>
                                                        ) : (
                                                            <StyledText fontSize="14px" fontWeight={600}>
                                                                Drop {activeTab === "Fields" ? "Field" : "Component"} Here
                                                            </StyledText>
                                                        )}
                                                    </OptionDropArea>
                                                </OptionContainer>
                                            );
                                        })}
                                    </div>
                                </ConnectingLineContainer>
                            )}
                        </div>
                    )}
                </RightPanel>
            </Container>
            <Footer>
                <Button theme={theme} $variant="cancel" onClick={onClose}>
                    <CloseIcon />
                    Cancel
                </Button>
                <Button
                    theme={theme}
                    onClick={handleSave}
                    type="submit"
                    $variant="add"
                >
                    <DocumentIcon />
                    Apply Conditions
                </Button>
            </Footer>
        </>
    );
};

export default ConditionalFieldModal;